<template>
  <div class="card rounded">
    <div class="card-header" :class="{ 'border-bottom-0': !currentValue.btn }">
      <CheckBoxField
        :initial-value="currentValue.btn"
        :scope="[...scope, `${hierarchy}_btn`]"
        disable-margin
        disable-changed
        @update="currentValue.btn = $event"
      />
    </div>

    <div v-if="currentValue.btn" class="card-body">
      <label class="mb-2" :for="scopeToId([...scope, `${hierarchy}_btn_type`])">
        {{ $t("backend.candidacy.components.jumbotron.btn_type") }}
      </label>
      <select
        v-model="currentValue.btn_type"
        :id="scopeToId([...scope, `${hierarchy}_btn_type`])"
        class="form-select mb-3"
        :name="scopeToName([...scope, `${hierarchy}_btn_type`])"
        @change="updateBtnType"
      >
        <option value="custom">{{ $t("backend.candidacy.components.jumbotron.custom") }}</option>
        <option value="signin">{{ $t("backend.candidacy.components.jumbotron.sign_in") }}</option>
        <option value="signup">{{ $t("backend.candidacy.components.jumbotron.sign_up") }}</option>
        <option value="candidates">{{ $t("backend.candidacy.components.jumbotron.candidates") }}</option>
      </select>

      <template v-if="currentValue.btn_type === 'custom'">
        <LocaleStringField
          :scope="[...scope, `${hierarchy}_btn_label`]"
          :locales="enabledLocales"
          :initial-value="currentValue.btn_label"
          :custom-label="$t(`backend.candidacy.components.jumbotron.${hierarchy}_btn_label`)"
          @update="currentValue.btn_label = $event"
        />

        <LocaleStringField
          :scope="[...scope, `${hierarchy}_btn_href`]"
          :locales="enabledLocales"
          :initial-value="currentValue.btn_href"
          :custom-label="$t(`backend.candidacy.components.jumbotron.${hierarchy}_btn_url`)"
          disable-margin
          @update="currentValue.btn_href = $event"
        />

        <IconSelector
          v-model:icon="currentValue.btn_icon"
          :scope="[...scope, `${hierarchy}_btn_icon`]"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, reactive, watch } from "vue";
import type { PropType, Locale, ButtonConfig, BtnHierarchy } from "@/types";
import CheckBoxField from "@/components/backend/candidacy/form_builder/form_items/shared/CheckBoxField.vue";
import LocaleStringField from "@/components/backend/candidacy/form_builder/form_items/shared/LocaleStringField.vue";
import IconSelector from "@/components/backend/candidacy/page_builder/IconSelector.vue";
import { scopeToId, scopeToName } from "@/lib/candidacy/form_builder/utils";
import i18n from "@/entrypoints/shared/i18n";

const { t } = i18n.global;

const emits = defineEmits(["update:button"]);

const props = defineProps({
  hierarchy: {
    type: String as PropType<BtnHierarchy>,
    required: true,
  },
  button: {
    type: Object as PropType<ButtonConfig>,
    required: true,
  },
  enabledLocales: {
    type: Array as PropType<Locale[]>,
    required: true,
  },
  scope: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

const currentValue = reactive<ButtonConfig>(props.button);

const updateBtnType = () => {
  props.enabledLocales.forEach((locale: Locale) => {
    switch (currentValue.btn_type) {
      case 'signin':
        currentValue.btn_label[locale] = t("js.candidacy.components.jumbotron.sign_in", locale);
        currentValue.btn_href[locale] = "#";
        currentValue.btn_icon = "user";
        break;
      case 'signup':
        currentValue.btn_label[locale] = t("js.candidacy.components.jumbotron.sign_up", locale);
        currentValue.btn_href[locale] = "#";
        currentValue.btn_icon = "user-plus";
        break;
      case 'candidates':
        currentValue.btn_label[locale] = t("js.candidacy.components.jumbotron.candidates", locale);
        currentValue.btn_href[locale] = "#";
        currentValue.btn_icon = "users";
        break;
      default:
        currentValue.btn_label[locale] = t("js.candidacy.components.jumbotron.example_button", locale);
        currentValue.btn_href[locale] = t("js.candidacy.components.jumbotron.example_url", locale);
        currentValue.btn_icon = "comments";
        break;
    };

    emits("update:button", currentValue);
  });
};

onMounted(() => {
  if (props.button.btn_type === "custom" && !!Object.keys(props.button.btn_label) && !!Object.keys(props.button.btn_href)) return;
  else updateBtnType();
});

watch(currentValue, () => {
  emits("update:button", currentValue);
});
</script>
